/**
 * BriefCard
 *
 * Compact card view of a single content brief with status and quick actions.
 */

import React from 'react';
import {
    Image,
    Video,
    Layers,
    Check,
    Edit2,
    FlaskConical,
    Clock,
    CheckCircle,
    Sparkles,
    PartyPopper
} from 'lucide-react';

const CONTENT_TYPE_ICONS = {
    image: Image,
    video: Video,
    carousel: Layers
};

const FUNNEL_COLORS = {
    TOFU: { bg: 'var(--info-100)', text: 'var(--info-700)', border: 'var(--info-500)' },
    MOFU: { bg: 'var(--warning-100)', text: 'var(--warning-700)', border: 'var(--warning-500)' },
    BOFU: { bg: 'var(--success-100)', text: 'var(--success-700)', border: 'var(--success-500)' }
};

const STATUS_CONFIG = {
    draft: { label: 'Draft', icon: Clock, color: 'var(--color-text-tertiary)' },
    ai_generated: { label: 'AI Generated', icon: Sparkles, color: 'var(--color-primary)' },
    refined: { label: 'Refined', icon: Edit2, color: 'var(--info-600)' },
    approved: { label: 'Approved', icon: CheckCircle, color: 'var(--success-600)' },
    generated: { label: 'Generated', icon: Image, color: 'var(--success-600)' },
    scheduled: { label: 'Scheduled', icon: Clock, color: 'var(--warning-600)' }
};

const BriefCard = ({ brief, onClick, onApprove, onUnapprove, isSelected }) => {
    const briefData = brief.brief || brief;
    const funnelStage = brief.funnelStage || 'TOFU';
    const outputType = brief.outputType || 'image';
    const status = brief.status || 'ai_generated';

    const ContentTypeIcon = CONTENT_TYPE_ICONS[outputType] || Image;
    const funnelColors = FUNNEL_COLORS[funnelStage] || FUNNEL_COLORS.TOFU;
    const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.ai_generated;
    const StatusIcon = statusConfig.icon;

    const headline = briefData.selectedHeadline || briefData.headline || 'Untitled';
    const angle = briefData.marketingAngle || briefData.angle || '';
    const pillarName = brief.pillarName || 'Unknown Pillar';
    const pillarColor = brief.pillarColor || '#6366F1';
    const brandColors = brief.brandColors || null;

    return (
        <div
            onClick={() => onClick?.(brief)}
            style={{
                background: 'var(--color-bg-primary)',
                border: `1px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderLeft: `4px solid ${pillarColor}`,
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-3)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
                boxShadow: isSelected ? '0 0 0 2px var(--color-primary-alpha)' : 'none'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = isSelected ? '0 0 0 2px var(--color-primary-alpha)' : 'none';
            }}
        >
            {/* Header Badges */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-2)'
            }}>
                {/* Funnel Stage Badge */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    fontSize: '10px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: funnelColors.bg,
                    color: funnelColors.text,
                    borderRadius: 'var(--radius-full)'
                }}>
                    {funnelStage}
                </span>

                {/* Content Type Badge */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    fontSize: '10px',
                    fontWeight: 500,
                    background: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-secondary)',
                    borderRadius: 'var(--radius-full)'
                }}>
                    <ContentTypeIcon size={10} />
                    {outputType}
                    {outputType === 'carousel' && briefData.carouselCards?.length > 0 && (
                        <span style={{
                            marginLeft: '2px',
                            padding: '0 4px',
                            fontSize: '9px',
                            background: 'var(--color-primary-alpha)',
                            color: 'var(--color-primary)',
                            borderRadius: 'var(--radius-full)'
                        }}>
                            {briefData.carouselCards.length}
                        </span>
                    )}
                </span>

                {/* A/B Test Indicator */}
                {brief.abTest?.isAbTest && (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        fontSize: '10px',
                        fontWeight: 500,
                        background: 'var(--warning-100)',
                        color: 'var(--warning-700)',
                        borderRadius: 'var(--radius-full)'
                    }}>
                        <FlaskConical size={10} />
                        A/B
                    </span>
                )}

                {/* Event Badge */}
                {brief.isEventBrief && (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        fontSize: '10px',
                        fontWeight: 500,
                        background: 'var(--error-100)',
                        color: 'var(--error-700)',
                        borderRadius: 'var(--radius-full)'
                    }}>
                        <PartyPopper size={10} />
                        {brief.eventName || 'Event'}
                    </span>
                )}

                {/* Story Arc Badge (carousel only) */}
                {outputType === 'carousel' && briefData.storyArcType && (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        fontSize: '10px',
                        fontWeight: 500,
                        background: 'var(--color-primary-alpha)',
                        color: 'var(--color-primary)',
                        borderRadius: 'var(--radius-full)'
                    }}>
                        {briefData.storyArcType.replace(/_/g, ' ')}
                    </span>
                )}

                {/* Status Badge - pushed to right */}
                <span style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '10px',
                    color: statusConfig.color
                }}>
                    <StatusIcon size={12} />
                </span>
            </div>

            {/* Headline */}
            <h4 style={{
                margin: '0 0 var(--space-1)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
            }}>
                {headline}
            </h4>

            {/* Marketing Angle */}
            {angle && (
                <p style={{
                    margin: '0 0 var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }}>
                    {angle}
                </p>
            )}

            {/* Footer */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 'var(--space-2)',
                paddingTop: 'var(--space-2)',
                borderTop: '1px solid var(--color-border)'
            }}>
                {/* Pillar Tag and Brand Colors */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    {/* Pillar Tag */}
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        color: 'var(--color-text-secondary)'
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: pillarColor
                        }} />
                        {pillarName}
                    </span>

                    {/* Brand Color Palette */}
                    {brandColors && (brandColors.primary || brandColors.secondary || brandColors.accent) && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            padding: '2px 4px',
                            background: 'var(--color-bg-tertiary)',
                            borderRadius: 'var(--radius-sm)'
                        }}
                        title={`Brand Colors: ${[
                            brandColors.primary && `Primary: ${brandColors.primary}`,
                            brandColors.secondary && `Secondary: ${brandColors.secondary}`,
                            brandColors.accent && `Accent: ${brandColors.accent}`
                        ].filter(Boolean).join(', ')}`}
                        >
                            {brandColors.primary && (
                                <span style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '2px',
                                    background: brandColors.primary,
                                    border: '1px solid rgba(0,0,0,0.1)'
                                }} />
                            )}
                            {brandColors.secondary && (
                                <span style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '2px',
                                    background: brandColors.secondary,
                                    border: '1px solid rgba(0,0,0,0.1)'
                                }} />
                            )}
                            {brandColors.accent && (
                                <span style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '2px',
                                    background: brandColors.accent,
                                    border: '1px solid rgba(0,0,0,0.1)'
                                }} />
                            )}
                        </div>
                    )}
                </div>

                {/* Quick Actions */}
                <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                    {status !== 'approved' && onApprove && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onApprove(brief.id);
                            }}
                            title="Approve"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                background: 'var(--success-100)',
                                color: 'var(--success-600)',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer'
                            }}
                        >
                            <Check size={14} />
                        </button>
                    )}
                    {status === 'approved' && onUnapprove && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onUnapprove(brief.id);
                            }}
                            title="Click to unapprove"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                background: 'var(--success-100)',
                                color: 'var(--success-600)',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer'
                            }}
                        >
                            <CheckCircle size={14} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BriefCard;
